import { Megaphone } from 'lucide-react';

import { Badge } from './Badge';
import { Button } from './Button';
import { Spinner } from './Spinner';

import type { ChangelogReleaseResponse } from '../lib/api-client';
import { useChangelog } from '../hooks/use-changelog';

import type { ChangelogFilters } from '../hooks/use-changelog';
import type { BadgeProps } from './Badge';

/* ─── Category → Badge Variant Mapping ─── */

const CATEGORY_BADGE_VARIANT: Record<string, BadgeProps['variant']> = {
  added: 'success',
  changed: 'info',
  fixed: 'warning',
  security: 'error',
  deprecated: 'not-started',
  removed: 'error',
};

const CATEGORY_LABEL: Record<string, string> = {
  added: 'Added',
  changed: 'Changed',
  fixed: 'Fixed',
  security: 'Security',
  deprecated: 'Deprecated',
  removed: 'Removed',
};

const CATEGORY_ORDER = ['added', 'changed', 'fixed', 'security', 'deprecated', 'removed'];

/* ─── Filter Chip Definitions ─── */

interface ChipOption {
  value: string | undefined;
  label: string;
}

const TIER_CHIPS: ChipOption[] = [
  { value: undefined, label: 'All' },
  { value: 'integrator', label: 'Integrator' },
  { value: 'end-user', label: 'End User' },
];

const STREAM_CHIPS: ChipOption[] = [
  { value: undefined, label: 'All' },
  { value: 'platform', label: 'Platform' },
  { value: 'n8n', label: 'LLM' },
];

const CATEGORY_CHIPS: ChipOption[] = [
  { value: undefined, label: 'All' },
  { value: 'added', label: 'Added' },
  { value: 'changed', label: 'Changed' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'security', label: 'Security' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'removed', label: 'Removed' },
];

/* ─── Helpers ─── */

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function groupEntriesByCategory(entries: { summary: string; category: string; tiers: string[] }[]) {
  const groups: Record<string, { summary: string; category: string; tiers: string[] }[]> = {};
  for (const entry of entries) {
    if (!groups[entry.category]) groups[entry.category] = [];
    groups[entry.category].push(entry);
  }
  return CATEGORY_ORDER.filter((cat) => groups[cat]).map((cat) => ({ category: cat, entries: groups[cat] }));
}

/* ─── Filter Chip Group ─── */

function FilterChipGroup({
  label,
  chips,
  activeValue,
  onSelect,
}: {
  label: string;
  chips: ChipOption[];
  activeValue: string | undefined;
  onSelect: (value: string | undefined) => void;
}) {
  const activeChipClass =
    'bg-sd-primary-subtle text-sd-text border-sd-primary/30 hover:bg-sd-primary-subtle/80';
  const inactiveChipClass =
    'bg-transparent text-sd-text-secondary border-sd-border/30 hover:bg-sd-surface-elevated/60 hover:border-sd-border/50';

  return (
    <div className="flex items-center gap-sd-1">
      <span className="text-sd-xs text-sd-text-muted font-medium uppercase tracking-wider mr-sd-1 select-none">
        {label}
      </span>
      {chips.map((chip) => {
        const isActive = chip.value === activeValue;
        return (
          <span
            key={chip.label}
            role="button"
            tabIndex={0}
            aria-label={`Filter by ${chip.label}`}
            aria-pressed={isActive}
            className={`inline-flex items-center px-sd-2 py-sd-1 text-sd-xs font-medium border rounded-sd-full cursor-pointer select-none transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-primary focus-visible:ring-offset-1 ${isActive ? activeChipClass : inactiveChipClass}`}
            onClick={() => onSelect(chip.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(chip.value);
              }
            }}
          >
            {chip.label}
          </span>
        );
      })}
    </div>
  );
}

/* ─── Release Card ─── */

function ReleaseCard({
  release,
  index,
}: {
  release: ChangelogReleaseResponse;
  index: number;
}) {
  const grouped = groupEntriesByCategory(release.entries);
  const revealClass = index < 8 ? `sd-reveal-${index + 1}` as string : 'sd-fade-in';

  return (
    <div className="relative pl-sd-6">
      {/* Timeline dot */}
      <div className="absolute left-0 top-[6px] w-[10px] h-[10px] rounded-sd-full border-2 border-sd-primary bg-sd-surface z-[1]" />

      <article
        className={`bg-sd-surface rounded-sd-md border border-sd-border/20 p-sd-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)] ${revealClass}`}
        aria-label={`Release from ${formatDate(release.date)}`}
      >
        {/* Date + Stream + Versions */}
        <div className="flex flex-wrap items-center gap-sd-2 mb-sd-3">
          <time className="text-sd-sm font-semibold text-sd-text" dateTime={release.date}>
            {formatDate(release.date)}
          </time>
          <Badge variant={release.stream === 'platform' ? 'info' : 'warning'} size="sm">
            {release.stream === 'platform' ? 'Platform' : 'LLM Worker'}
          </Badge>
        </div>

        {/* Version pills */}
        <div className="flex flex-wrap gap-sd-1 mb-sd-3">
          {release.packages &&
            Object.entries(release.packages).map(([pkg, ver]) => (
              <span
                key={pkg}
                className="inline-flex items-center text-sd-xs text-sd-text-muted bg-sd-surface-elevated px-sd-2 py-0.5 rounded-sd-full font-mono"
              >
                {pkg}{' '}
                <span className="text-sd-text-secondary ml-0.5">{ver}</span>
              </span>
            ))}
          {release.version && (
            <span className="inline-flex items-center text-sd-xs text-sd-text-muted bg-sd-surface-elevated px-sd-2 py-0.5 rounded-sd-full font-mono">
              <span className="text-sd-text-secondary">{release.version}</span>
            </span>
          )}
        </div>

        {/* Entries grouped by category */}
        <div className="space-y-sd-3">
          {grouped.map(({ category, entries }) => (
            <div key={category}>
              <div className="mb-sd-1">
                <Badge variant={CATEGORY_BADGE_VARIANT[category] ?? 'info'} size="sm">
                  {CATEGORY_LABEL[category] ?? category}
                </Badge>
              </div>
              <ul className="space-y-sd-1 ml-sd-1">
                {entries.map((entry, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-sd-2 text-sd-sm text-sd-text leading-relaxed"
                  >
                    <span className="mt-[7px] w-[4px] h-[4px] rounded-sd-full bg-sd-text-muted shrink-0" />
                    {entry.summary}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </article>
    </div>
  );
}

/* ─── Main Component ─── */

export function ChangelogView() {
  const { status, error, releases, hasMore, filters, setFilters, loadMore, reload } =
    useChangelog();

  const handleFilterChange = (key: keyof ChangelogFilters, value: string | undefined) => {
    setFilters({ ...filters, [key]: value });
  };

  /* Loading */
  if (status === 'loading' && releases.length === 0) {
    return (
      <div className="flex items-center justify-center h-full bg-sd-background">
        <Spinner size="lg" />
      </div>
    );
  }

  /* Error */
  if (status === 'error' && releases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-sd-4 text-center bg-sd-background">
        <p className="text-sd-error mb-sd-3 text-sd-sm">{error ?? 'Failed to load changelog'}</p>
        <Button variant="secondary" size="sm" onClick={reload}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-sd-background overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-sd-background/95 backdrop-blur-sm border-b border-sd-border/20">
        <div className="px-sd-4 pt-sd-4 pb-sd-3">
          <div className="flex items-center gap-sd-2 mb-sd-3">
            <Megaphone size={18} className="text-sd-text-muted" />
            <h2 className="text-sd-lg font-semibold text-sd-text tracking-tight">
              What&apos;s New
            </h2>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-sd-2">
            <FilterChipGroup
              label="Tier"
              chips={TIER_CHIPS}
              activeValue={filters.tier}
              onSelect={(v) => handleFilterChange('tier', v)}
            />
            <FilterChipGroup
              label="Stream"
              chips={STREAM_CHIPS}
              activeValue={filters.stream}
              onSelect={(v) => handleFilterChange('stream', v)}
            />
            <FilterChipGroup
              label="Type"
              chips={CATEGORY_CHIPS}
              activeValue={filters.category}
              onSelect={(v) => handleFilterChange('category', v)}
            />
          </div>
        </div>
      </div>

      {/* Release timeline */}
      <div className="flex-1 min-h-0">
        {releases.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center h-full p-sd-8 text-center">
            <div className="w-12 h-12 rounded-sd-lg bg-sd-surface-elevated flex items-center justify-center mb-sd-3">
              <Megaphone size={20} className="text-sd-text-muted" />
            </div>
            <p className="text-sd-base font-semibold text-sd-text mb-sd-1">No releases found</p>
            <p className="text-sd-sm text-sd-text-muted">
              Try adjusting the filters to see more releases.
            </p>
          </div>
        ) : (
          <div className="px-sd-4 py-sd-4">
            {/* Timeline line + cards */}
            <div className="relative">
              {/* Vertical timeline line */}
              <div className="absolute left-[4px] top-[10px] bottom-0 w-[2px] bg-sd-border/40" />

              <div className="space-y-sd-4">
                {releases.map((release, i) => (
                  <ReleaseCard key={release.id} release={release} index={i} />
                ))}
              </div>
            </div>

            {/* Load more */}
            {hasMore && (
              <div className="flex justify-center mt-sd-4 pl-sd-6">
                <Button variant="ghost" size="sm" onClick={loadMore}>
                  Load more releases
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
